import { useEffect, useState } from 'react';
import { adresseApi } from '../api/client';
import { piecesApi } from '../api/resources';
import { dateSuisse } from '../lib/dates';
import { tr, trf } from '../lib/i18n';
import { reduitLImage } from '../lib/image';
import { Btn } from './ui/Btn.jsx';
import { _inp } from './ui/Inputs.jsx';
import { ouvreVisionneuse } from './Visionneuse.jsx';
import { C } from '../theme/palette';

/** Ouvre une pièce jointe dans la visionneuse, sinon dans un onglet. */
export function ouvrePiece(piece) {
  const adresse = adresseApi('/api/pieces/' + piece.id + '/contenu');
  if (!ouvreVisionneuse({ adresse, titre: piece.libelle })) window.open(adresse, '_blank');
}

/**
 * Les documents joints à une pièce : contrat, PV signé, bon de livraison…
 *
 * <p>Le patron joint le fichier tel qu'il l'a : un PDF reçu par e-mail ou une
 * photo prise sur place. Il choisit d'abord ce que c'est, le retrouve dans la
 * liste et l'ouvre dans SwissTech, avec « ← Retour ».
 *
 * <p>Plusieurs blocs peuvent partager les pièces d'un même document (les
 * pièces « CHANTIER » d'un chantier, par exemple) : chacun ne montre que les
 * siennes, celles dont le libellé commence par une de ses sortes.
 *
 * @param {object} props propriétés
 * @param {string} props.genre genre des pièces (CHANTIER, VEHICULE, CLIENT…)
 * @param {number|string} props.documentId document concerné ; tant qu'il
 *   n'est pas enregistré, le bloc le dit au lieu de proposer l'envoi
 * @param {string[]} props.sortes ce qu'on peut joindre, la première par défaut
 * @param {string} [props.titre] intitulé du bloc
 * @param {boolean} [props.filtre] ne montrer que les pièces de ces sortes
 * @returns {import('react').ReactElement} la liste et le bouton
 */
export function DocumentsJoints({ genre, documentId, sortes, titre, filtre = false }) {
  const [pieces, setPieces] = useState([]);
  const [sorte, setSorte] = useState(sortes[0]);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState('');
  const enregistre = typeof documentId === 'number';

  const relit = () =>
    enregistre
      ? Promise.resolve()
          .then(() => piecesApi.liste(genre, documentId))
          .then((p) => setPieces(p || []))
          .catch(() => {})
      : Promise.resolve();
  useEffect(() => {
    relit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genre, documentId]);

  const visibles = filtre
    ? pieces.filter((p) => sortes.some((s) => String(p.libelle || '').startsWith(s)))
    : pieces;

  const joint = async (e) => {
    const fichier = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!fichier) return;
    setEnvoi(true);
    setErreur('');
    try {
      const pret = fichier.type.startsWith('image/') ? await reduitLImage(fichier) : fichier;
      await piecesApi.depose(genre, documentId, pret, sorte);
      await relit();
    } catch (err) {
      setErreur(err?.message || tr('Le document n’a pas pu être joint.'));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 800, color: C.muted, marginBottom: 4 }}>
        {titre || tr('📎 DOCUMENTS')}
      </div>
      {visibles.map((p) => (
        <div
          key={p.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 0',
            borderTop: '1px solid ' + C.border,
            fontSize: 11,
          }}
        >
          <span style={{ flex: 1, fontWeight: 700 }}>
            {(String(p.typeMedia || '').includes('pdf') ? '📄 ' : '🖼 ') +
              tr(p.libelle || 'Document')}
          </span>
          <span style={{ color: C.muted, fontSize: 9 }}>{dateSuisse(p.date)}</span>
          <Btn label="📎 Voir" sm ghost color={C.navy} onClick={() => ouvrePiece(p)} />
          <Btn
            label="🗑"
            sm
            ghost
            color={C.red}
            onClick={() => {
              if (window.confirm(trf('Retirer « {0} » ?', tr(p.libelle || 'Document')))) {
                piecesApi.retire(p.id).then(relit);
              }
            }}
          />
        </div>
      ))}
      {!enregistre ? (
        <div style={{ fontSize: 9.5, color: C.muted, padding: '4px 0' }}>
          {tr('Enregistre d’abord, puis joins les documents.')}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
          {sortes.length > 1 && (
            <select
              aria-label={tr('Sorte de document')}
              value={sorte}
              onChange={(e) => setSorte(e.target.value)}
              style={{ ..._inp, flex: 1, padding: '8px 9px' }}
            >
              {sortes.map((s) => (
                <option key={s} value={s}>
                  {tr(s)}
                </option>
              ))}
            </select>
          )}
          <label
            style={{
              flex: 1,
              textAlign: 'center',
              cursor: 'pointer',
              border: '1.5px dashed ' + C.teal,
              color: C.teal,
              borderRadius: 9,
              padding: '8px 6px',
              fontSize: 11,
              fontWeight: 800,
            }}
          >
            {envoi ? tr('⏳ Envoi…') : tr('📎 Joindre (PDF ou photo)')}
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={joint}
              style={{ display: 'none' }}
              disabled={envoi}
            />
          </label>
        </div>
      )}
      {erreur && (
        <div role="alert" style={{ color: C.red, fontSize: 10, marginTop: 4 }}>
          ✕ {erreur}
        </div>
      )}
    </div>
  );
}

/**
 * Ouvre dans la visionneuse un fichier gardé en adresse de données (le
 * justificatif d'une dépense, par exemple).
 *
 * @param {string} donnees adresse « data:… »
 * @param {string} [titre] titre de la visionneuse
 */
export function ouvreDonnees(donnees, titre) {
  const m = String(donnees || '').match(/^data:([^;,]*)(;base64)?,(.*)$/);
  if (!m) return;
  const brut = m[2] ? atob(m[3]) : decodeURIComponent(m[3]);
  const octets = new Uint8Array(brut.length);
  for (let i = 0; i < brut.length; i += 1) octets[i] = brut.charCodeAt(i);
  const blob = new Blob([octets], { type: m[1] || 'application/octet-stream' });
  if (!ouvreVisionneuse({ blob, titre })) window.open(URL.createObjectURL(blob), '_blank');
}
